import { useContext, useEffect, useMemo, useState } from 'react';
import { useThemeMode } from '../../../../../providers/theme-provider';
import Utils from '../../../../../utils/immersive_utils';

import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Typography, Divider } from '@mui/material';

import { RiskPanelCard } from '../../../components/cards/risk-panel-card';
import {
  IPortfolioPerformanceAccountReport,
  IPortfolioPerformanceAccounts,
  PortfolioPerformanceContext,
} from '../../../../../providers/portfolio-performance';
import { RiskProvider } from '../../../../../providers/portfolio-performance/risk';
import { TransactionsProvider } from '../../../../../providers/portfolio-performance/transactions';
import { TransactionsCard } from '../../../components/cards/transactions-card';
import { PositionsCard } from '../../../components/cards/positions-card';
import { PerformanceCard } from '../../../components/cards/performance-card';
import { AccountEquityCard } from '../../../components/cards/account-equity-card';
import { LiveRiskMetricsBand } from '../../../components/cards/live-risk-band';
import { DailyReturnStatsBand } from '../../../components/daily-return-stats-band';
import {
  computeDailyStats,
  getFirstDayPv,
  MIN_RATIO_SAMPLE,
} from '../../../../../providers/portfolio-performance/compute-daily-stats';
import { buildStatsHistorical } from '../../../../../providers/portfolio-performance/build-stats-historical';
import {
  dilettaPanelSx,
  DILETTA_TITLE,
  DILETTA_DIVIDER,
} from '../../../../../components/v2/widgets/exotics-shared';
import { CardTabs } from '../../../../../components/card-tabs';
import DeribitIcon from '../../../../../assets/icons/deribit-icon.svg';
import OKXIcon from '../../../../../assets/icons/okx-icon.svg';
import CryptoIcon from '../../../../../components/v2/crypto-icon';

type PortfolioPerformanceAccountCardType = {
  account: IPortfolioPerformanceAccounts;
  isOpen: boolean;
  onToggle: () => void;
  // Set for synthetic "buy and hold" reference strategies (BTC/ETH) — a
  // client-side-only report, not backed by a real portfolio_performance
  // account. Skips the context-based fetch and disables the Market
  // Risk/Transactions tabs, which don't apply to a plain spot position.
  referenceReport?: IPortfolioPerformanceAccountReport | null;
};

const formatValue = (value: string | null, fallbackValue: string) => {
  if (value === null || value === undefined || value === '') return fallbackValue;

  return value;
};

const MetricChip = ({
  label,
  value,
  blurred,
}: {
  label: string;
  value: string;
  blurred?: boolean;
}) => {
  const { resolvedMode } = useThemeMode();
  const labelColor = resolvedMode === 'dark' ? 'rgba(194,229,255,0.5)' : 'rgba(9,45,58,0.5)';
  const valueColor = resolvedMode === 'dark' ? DILETTA_TITLE : '#092D3A';
  return (
    <Box sx={{ minWidth: 64 }}>
      <Typography
        sx={{
          fontSize: 9,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: labelColor,
          mb: 0.25,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 600,
          color: valueColor,
          filter: blurred ? 'blur(6px)' : 'none',
          transition: 'filter 150ms ease',
          userSelect: blurred ? 'none' : 'auto',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

export const PortfolioPerformanceAccountCard = ({
  account,
  isOpen,
  onToggle,
  referenceReport,
}: PortfolioPerformanceAccountCardType) => {
  const { resolvedMode } = useThemeMode();
  const isReference = referenceReport !== undefined;
  const {
    fetchReport,
    reportsByAccount,
    loadingReportsByAccount,
    errorReportsByAccount,
    isNamesBlurred,
  } = useContext(PortfolioPerformanceContext);
  const report = isReference ? referenceReport : reportsByAccount[account.name];
  const isLoadingReport = isReference ? false : loadingReportsByAccount[account.name];
  const hasErrorReport = isReference ? false : errorReportsByAccount[account.name];
  const fallbackValue = isLoadingReport ? '...' : '-';

  // TODO: temporary — only 2026 has clean full-year data across REAL accounts
  // right now, so limit their charts to it for this look. Remove once prior
  // years are reconciled and ready to show. Reference/benchmark reports have
  // no such data-quality gap (just raw market closes), so they're exempt —
  // their own charts show whatever multi-year history
  // use-reference-strategy-reports.ts fetched.
  //
  // Real accounts also patch 2026's annual_return with the YTD figure
  // client-side until Nikhil's backend fix (ticket drafted) surfaces it in
  // `historical` directly, and inject TODAY's point from the live
  // daily_return/mtd_return metrics — the backend's historical.days only
  // ever contains CLOSED days (confirmed live — today's key is simply
  // absent until finalized a day later). Rather than reinventing TWR/HWM
  // compounding client-side to fill that gap (real path-dependence risk —
  // see notional_scaling HWM notes), reuse the already-fetched LIVE metrics
  // the backend computes for exactly this purpose (all three already shown
  // as the "Daily (TWR)"/"MTD (TWR)" chips below). The report re-polls every
  // 5 minutes (REPORT_POLLING_INTERVAL_MS), so today's injected point
  // updates right along with it — no separate timer needed here.
  //
  // buildStatsHistorical does this exact transform (shared with the
  // Overview table's own Sharpe/Sortino/MaxDD columns below, so the two can
  // never drift apart — see its header comment). Reference reports skip it
  // here (full history, for the charts) but still run through it separately
  // for `dailyStats` below.
  const historical = useMemo(
    () => (isReference ? report?.historical : buildStatsHistorical(report, false)),
    [report, isReference],
  );

  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (isReference) return;
    if (report || isLoadingReport || hasErrorReport) return;

    fetchReport(account);
  }, [account, fetchReport, hasErrorReport, isLoadingReport, report, isReference]);

  // "Capital" shows the PV on the first day of the window actually on screen
  // (2026-only right now), not the account's true inception capital — the
  // two would otherwise mismatch since the charts don't go back that far.
  const startingCapitalPv = useMemo(() => getFirstDayPv(historical), [historical]);
  const capitalPrecision =
    report?.denomination === 'USD' || report?.denomination === 'USDC' ? 2 : 4;
  const startingCapitalDisplay =
    startingCapitalPv === null ? null : Utils.formatNumber(startingCapitalPv, capitalPrecision);

  const dataTop = [
    {
      label: 'Team',
      value: formatValue(report?.team ?? null, fallbackValue),
      blurred: isNamesBlurred,
    },
    { label: 'Strategy', value: formatValue(report?.strategy ?? null, fallbackValue) },
    { label: 'Capital', value: formatValue(startingCapitalDisplay, fallbackValue) },
    { label: 'NPV', value: formatValue(report?.npv ?? null, fallbackValue) },
  ];

  // Sharpe/Sortino/MaxDD recomputed off the same 2026-only daily series
  // (real accounts) rather than the backend's inception-to-date figures.
  // Uses buildStatsHistorical directly rather than the `historical` above —
  // for reference accounts `historical` is the FULL multi-year series (for
  // the charts), but its stats still need the 2026 slice, same as real
  // accounts and same as the Overview table's columns, so every row in that
  // table means the same thing.
  const dailyStats = useMemo(
    () => computeDailyStats(buildStatsHistorical(report, isReference)),
    [report, isReference],
  );
  const formatRatio = (value: number | null) => (value === null ? null : value.toFixed(2));
  const formatPct = (value: number | null) =>
    value === null ? null : `${(value * 100).toFixed(2)}%`;

  // The annualized ratios are suppressed until MIN_RATIO_SAMPLE daily returns
  // exist — a freshly-opened account (e.g. 1-2 days) would otherwise show a
  // nonsensical Sharpe. Show "Not enough data" instead, but keep the plain
  // loading fallback ('...') while the report is still in flight.
  const insufficientRatioSample = !isLoadingReport && dailyStats.sampleSize < MIN_RATIO_SAMPLE;
  const formatRatioValue = (value: number | null) =>
    insufficientRatioSample ? 'Not enough data' : formatValue(formatRatio(value), fallbackValue);

  const dataBottom = [
    { label: 'Daily (TWR)', value: formatValue(report?.daily ?? null, fallbackValue) },
    { label: 'MTD (TWR)', value: formatValue(report?.mtd ?? null, fallbackValue) },
    { label: 'YTD (TWR)', value: formatValue(report?.ytd ?? null, fallbackValue) },
    {
      label: 'MaxDD (2026, Daily)',
      value: formatValue(formatPct(dailyStats.maxDrawdownPct), fallbackValue),
    },
    {
      label: 'Sharpe (2026, Daily)',
      value: formatRatioValue(dailyStats.sharpe),
    },
    {
      label: 'Sortino (2026, Daily)',
      value: formatRatioValue(dailyStats.sortino),
    },
  ];

  return (
    <Box sx={{ ...dilettaPanelSx, mb: 2 }}>
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          p: 2,
          cursor: 'pointer',
        }}
      >
        {isReference ? (
          <Box sx={{ mt: 0.5, flexShrink: 0 }}>
            <CryptoIcon symbol={account.symbols[0]} size={28} />
          </Box>
        ) : (
          <Box
            component="img"
            src={account.exchange === 'deribit' ? DeribitIcon : OKXIcon}
            alt={account.name}
            sx={{ width: 28, height: 28, mt: 0.5, flexShrink: 0 }}
          />
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 15,
              fontWeight: 700,
              color: resolvedMode === 'dark' ? DILETTA_TITLE : '#092D3A',
              mb: 1.25,
              filter: isNamesBlurred ? 'blur(6px)' : 'none',
              transition: 'filter 150ms ease',
              userSelect: isNamesBlurred ? 'none' : 'auto',
            }}
          >
            {account.name || ''}
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, mb: 1.25 }}>
            {dataTop.map((item) => (
              <MetricChip
                key={item.label}
                label={item.label}
                value={item.value}
                blurred={item.blurred}
              />
            ))}
          </Box>

          <Divider sx={{ borderColor: DILETTA_DIVIDER, mb: 1.25 }} />

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 1 }}>
            {dataBottom.map((item) => (
              <MetricChip key={item.label} label={item.label} value={item.value} />
            ))}
          </Box>

          {/* Free-text strategy blurb from the backend (account.description) — many
              accounts don't have one set yet, so this is omitted entirely rather than
              showing an empty row. */}
          {report?.description && (
            <Typography
              sx={{
                fontSize: 12,
                fontStyle: 'italic',
                lineHeight: 1.5,
                mt: 1.25,
                color: resolvedMode === 'dark' ? 'rgba(194,229,255,0.7)' : 'rgba(9,45,58,0.65)',
              }}
            >
              {report.description}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            color: resolvedMode === 'dark' ? 'rgba(194,229,255,0.6)' : 'rgba(9,45,58,0.5)',
            fontSize: 14,
            mt: 0.5,
            flexShrink: 0,
          }}
        >
          <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
        </Box>
      </Box>

      {/* Providers mount with the card so data fetches begin before the user opens the panel.
          Content below mounts unconditionally too (display-toggled, not unmounted) so the
          charts/tables are already built by the time the user opens the card — otherwise the
          first click pays for mounting two chart libraries and a full heatmap synchronously.
          Reference strategies skip both providers entirely — there's no real account behind
          them for Risk/Transactions to fetch, and their tabs are disabled below anyway. */}
      {(() => {
        const tabsAndContent = (
          <Box sx={{ display: isOpen ? 'block' : 'none' }}>
            <CardTabs
              value={activeTab}
              onChange={setActiveTab}
              tabs={[
                { label: 'Performance' },
                { label: 'Market Risk', disabled: isReference },
                { label: 'Positions', disabled: isReference },
                { label: 'Transactions', disabled: isReference },
              ]}
            />

            <Box sx={{ p: 2 }}>
                <Box sx={{ display: activeTab === 0 ? 'block' : 'none' }}>
                  {!isReference && <LiveRiskMetricsBand />}
                  <Box
                    sx={{
                      border:
                        resolvedMode === 'dark'
                          ? '1px solid #026aa2'
                          : '1px solid rgba(9, 45, 58, 0.12)',
                      backgroundColor:
                        resolvedMode === 'dark' ? 'rgba(40, 114, 226, 0.10)' : '#ffffff',
                      borderRadius: '16px',
                      boxShadow: '0px 1px 2px 0px #00000059, 0px 2px 6px 2px #00000026',
                      p: 2.5,
                      mb: 2,
                    }}
                  >
                    <AccountEquityCard
                      data={historical}
                      denomination={report?.denomination}
                      isLoading={isLoadingReport}
                    />
                  </Box>
                  <DailyReturnStatsBand stats={dailyStats} />
                  <PerformanceCard
                    data={historical}
                    isLoading={isLoadingReport}
                    accountName={account.name}
                    calculatedAt={report?.calculatedAt}
                  />
                </Box>

                {!isReference && (
                  <>
                    <Box sx={{ display: activeTab === 1 ? undefined : 'none' }}>
                      <RiskPanelCard />
                    </Box>

                    <Box sx={{ display: activeTab === 2 ? undefined : 'none' }}>
                      <PositionsCard
                        positions={report?.positions ?? []}
                        totalPv={report?.positionsTotalPv ?? null}
                        denomination={report?.denomination}
                        isLoading={isLoadingReport}
                      />
                    </Box>

                    <Box sx={{ display: activeTab === 3 ? undefined : 'none' }}>
                      <TransactionsCard />
                    </Box>
                  </>
                )}
            </Box>
          </Box>
        );

        if (isReference) return tabsAndContent;

        return (
          <RiskProvider account={account}>
            <TransactionsProvider account={account}>{tabsAndContent}</TransactionsProvider>
          </RiskProvider>
        );
      })()}
    </Box>
  );
};
